import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { createRef } from 'react';
import CopyToClipboardButton from './CopyToClipboardButton.js';
import EditableFieldWithWarnings from './EditableFieldWithWarnings.js';
import { buildArraySchemaWarningPath } from './PathUtils.js';

export default function DocumentsOverview({ caseInfo, setCaseInfo, fileNamesToFilePropertiesMap, displayedDocument, setDisplayedDocument, setSnackBarMessage }) {
    const tableRef = createRef();
    var documentKeysToDisplay = caseInfo !== null && 'documents' in caseInfo ?
        Object.keys(caseInfo['documents']).filter(docKey => fileNamesToFilePropertiesMap[caseInfo['documents'][docKey]['filename']]['selected']) :
        [];

    const isCategorizeInProgress = Object.values(caseInfo['documents'] || {}).some(doc => doc['analysis_queue']?.includes('categorize'));

    // Helper function to build paths for overview fields (new array schema for needs_review/caveats)
    const buildOverviewFieldPaths = (documentKey, fieldName) => {
        const base = `$.documents['${documentKey}'].analysis.overview[0]`;
        return {
            textPath: `${base}.${fieldName}`
            // Remove these warnings since they are not actionable
            // needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
            // caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
        };
    };

    // Helper function to build paths for summary field with special handling
    const buildSummaryFieldPaths = (documentKey) => {
        const base = `$.documents['${documentKey}'].analysis.overview[0]`;
        const fieldName = 'initial_summary';
        return {
            textPath: `${base}.${fieldName}`,
            needsReviewPath: buildArraySchemaWarningPath(base, fieldName, 'needs_review'),
            caveatsPath: buildArraySchemaWarningPath(base, fieldName, 'caveats')
        };
    };

    var tableBodyRows = documentKeysToDisplay
        .sort((documentKey1, documentKey2) =>
            Date.parse(caseInfo['documents'][documentKey1]?.analysis?.overview?.[0]?.date) -
            Date.parse(caseInfo['documents'][documentKey2]?.analysis?.overview?.[0]?.date))
        .map(documentKey => {
            const docInfo = caseInfo['documents'][documentKey];
            const filename = docInfo?.['filename'];
            let isProcessing = docInfo?.['status']?.includes('processing');

            return (
                <TableRow
                    key={documentKey}
                    className={documentKey === displayedDocument?.['key'] ? "selected" : ""}
                    sx={{
                        '&:hover .source-view-icon': {
                            visibility: 'visible',
                            opacity: 0.7
                        }
                    }}
                >
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)'
                    }}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                                component="span"
                                title="Click to view source document"
                                sx={{
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    fontSize: '0.9rem',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': 1
                                    });
                                }}
                            >
                                {filename}
                            </Typography>
                            <IconButton
                                className="source-view-icon"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDisplayedDocument({
                                        'key': documentKey,
                                        'page': 1
                                    });
                                }}
                                sx={{
                                    padding: '2px',
                                    visibility: 'hidden',
                                    opacity: 0,
                                    transition: 'opacity 0.2s, visibility 0.2s',
                                    '&:hover': {
                                        opacity: 1,
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                                title="Click to view source document"
                            >
                                <VisibilityIcon />
                            </IconButton>
                        </Box>
                        <img
                            src="/loading.gif"
                            alt="loading..."
                            title="Processing..."
                            style={{
                                marginLeft: "8px",
                                height: "16px",
                                verticalAlign: "middle",
                                display: isProcessing ? "initial" : "none"
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)'
                    }}>
                        <EditableFieldWithWarnings
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildOverviewFieldPaths(documentKey, 'date')}
                            setSnackBarMessage={setSnackBarMessage}
                            onWarningClick={(e) => {
                                e.stopPropagation();
                                setDisplayedDocument({
                                    'key': documentKey,
                                    'page': 1
                                });
                            }}
                        />
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)'
                    }}>
                        {renderCategoryOrProcessingMessage(documentKey, isProcessing, docInfo)}
                    </TableCell>
                    <TableCell sx={{
                        color: 'var(--text-primary)',
                        borderBottom: '1px solid var(--border-color)'
                    }}>
                        {renderSummaryOrProcessingMessage(documentKey, isProcessing, docInfo)}
                    </TableCell>
                </TableRow >)
        })

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {isCategorizeInProgress && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/loading.gif"
                                alt="loading..."
                                title="Processing..."
                                style={{
                                    marginRight: "8px",
                                    height: "16px",
                                    verticalAlign: "middle"
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontStyle: 'italic',
                                    fontSize: '1.1rem',
                                    fontWeight: 500
                                }}
                            >
                                Documents are being categorized and summarized...
                            </Typography>
                        </Box>
                    )}
                </Box>
                <CopyToClipboardButton nodeRef={tableRef} setSnackBarMessage={setSnackBarMessage} buttonText="Copy Documents Overview" />
            </Box>
            <Table
                ref={tableRef}
                sx={{
                    minWidth: 650,
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-light)'
                }}
                aria-label="documents overview table"
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'var(--surface-color)' }}>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Document Name
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "150px"
                        }}>
                            Document Date
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px',
                            minWidth: "150px"
                        }}>
                            Category
                        </TableCell>
                        <TableCell sx={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            borderBottom: '2px solid var(--border-color)',
                            padding: '16px 12px'
                        }}>
                            Summary
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableBodyRows}
                </TableBody>
            </Table>
        </Box>
    )

    function renderCategoryOrProcessingMessage(documentKey, isProcessing, documentInfo) {
        // console.log(documentInfo);
        const documentType = documentInfo?.analysis?.overview?.[0]?.document_type || documentInfo?.analysis?.overview?.[0]?.category;
        if (documentType) {
            if (documentInfo?.analysis?.overview?.[0]?.document_type) {
                return (<EditableFieldWithWarnings
                    onWarningClick={(e) => {
                        e.stopPropagation();
                        setDisplayedDocument({
                            'key': documentKey,
                            'page': 1
                        });
                    }}
                    caseInfo={caseInfo}
                    setCaseInfo={setCaseInfo}
                    {...buildOverviewFieldPaths(documentKey, 'document_type')}
                    setSnackBarMessage={setSnackBarMessage}
                />)
            } else {
                return documentType;
            }
        } else {
            return isProcessing ? 'Processing' : 'Unknown'
        }
    }

    function renderSummaryOrProcessingMessage(documentKey, isProcessing, documentInfo) {
        let summary = documentInfo?.analysis?.overview?.[0]?.initial_summary || documentInfo?.analysis?.overview?.[0]?.summary;

        if (summary) {
            if (documentInfo?.analysis?.overview?.[0]?.initial_summary) {
                return (
                    <Box>
                        <EditableFieldWithWarnings
                            onWarningClick={(e) => {
                                e.stopPropagation();
                                setDisplayedDocument({
                                    'key': documentKey,
                                    'page': 1
                                });
                            }}
                            caseInfo={caseInfo}
                            setCaseInfo={setCaseInfo}
                            {...buildSummaryFieldPaths(documentKey)}
                            setSnackBarMessage={setSnackBarMessage}
                        />
                    </Box>
                );
            } else {
                return (
                    <Box>
                        <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                            {summary}
                        </Typography>
                    </Box>
                );
            }
        } else {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img
                        src="/loading.gif"
                        alt="loading..."
                        title="Processing..."
                        style={{
                            marginRight: "8px",
                            height: "16px",
                            verticalAlign: "middle",
                            display: documentInfo?.['status']?.includes('processing') ? "initial" : "none",
                            opacity: 0.7
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontStyle: 'italic'
                        }}
                    >
                        {(isProcessing ?
                            (documentInfo?.['status']?.includes('ocr') ?
                                'OCR: Extracting text (~10 secs per page)' :
                                'Analyzing...') :
                            '')}
                    </Typography>
                </Box>
            );
        }
    }
}